/**
 * The specialty switcher.
 *
 * rev v11.0.0 · Phase 2 / C2
 *
 * One control that changes the clinical context of the whole application.
 *
 * It is a dropdown rather than a permanent bar for one reason: the
 * specialty is a CONTEXT, not a navigation item. A clinician switches it
 * when they change their mind about the patient in front of them, and then
 * forgets about it.
 */
import { useState } from "react";
import { Link, useRouter } from "@tanstack/react-router";
import { Check, ChevronDown, Stethoscope } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { useSpecialty } from "@/hooks/use-specialty";
import { ROUTES, asRoutePath } from "@/lib/routes";
import { useSpecialtyFlag } from "@/hooks/use-specialty-flag";

export function SpecialtySwitcher() {
  const { specialty, specialties, setSpecialty } = useSpecialty();
  const router = useRouter();
  const flag = useSpecialtyFlag(specialty.id);
  const [busy, setBusy] = useState(false);

  const select = async (id: string) => {
    setBusy(true);
    setSpecialty(id);
    try {
      await router.navigate({
        to: asRoutePath(ROUTES.specialty),
        params: { specialty: id } as never,
      });
    } catch {
      /* the context is already set; a navigation failure is not fatal */
    } finally {
      setBusy(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-2"
          disabled={busy}
          aria-label="Changer de spécialité"
        >
          <Stethoscope className="h-3.5 w-3.5" />
          <span className="max-w-[10rem] truncate">{specialty.label}</span>
          {flag.enabled && (
            <Badge className="ml-1 h-4 px-1 text-[9px]">v2</Badge>
          )}
          <ChevronDown className="h-3 w-3 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72 max-h-[70vh] overflow-y-auto">
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          Espace clinique
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {specialties.map((s) => (
          <DropdownMenuItem
            key={s.id}
            onSelect={() => {
              void select(s.id);
            }}
            className="flex items-center justify-between gap-2"
          >
            <span className="min-w-0">
              <span className="block truncate">{s.label}</span>
              <span className="block truncate text-[10px] text-muted-foreground">
                {s.featureFlag}
              </span>
            </span>
            {s.id === specialty.id && <Check className="h-3.5 w-3.5 shrink-0" />}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to={asRoutePath(ROUTES.specialtyAdoption)}>État du déploiement</Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
